import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Spinner, Button, Modal, Table } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Search, List, Grid } from 'lucide-react';

// Moment auf Deutsch setzen
moment.locale('de');
const localizer = momentLocalizer(moment);

const CALENDAR_ID = 'c_g1fmt6s44qqhil9aib1pcf3r4s@group.calendar.google.com';
const API_KEY = 'AIzaSyC6HDB44mLIJeF8UlWhW-xBV-7BZaeWjH0';

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Initialize viewMode based on screen width
  const [viewMode, setViewMode] = useState(() => {
    // Default to list view on mobile, grid view on desktop
    return window.innerWidth < 768 ? 'list' : 'grid';
  });

  // Termine von der Google Calendar API laden - memoized fetch function
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
        CALENDAR_ID
      )}/events?key=${API_KEY}&timeMin=${now}&singleEvents=true&orderBy=startTime&maxResults=250`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.error) {
        setError(data.error.message);
      } else {
        setEvents(data.items || []);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Kalenderdaten:', err);
      setError('Fehler beim Laden der Kalenderdaten.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    
    // Add event listener for window resize with debounce
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth < 768 && viewMode === 'grid') {
          setViewMode('list');
        }
      }, 250);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [fetchEvents]);

  // Navigation: Vorherige/ Nächste/ Heute
  const handlePrev = useCallback(() => {
    setCurrentDate(prev => moment(prev).subtract(1, 'month').toDate());
  }, []);

  const handleNext = useCallback(() => {
    setCurrentDate(prev => moment(prev).add(1, 'month').toDate());
  }, []);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Konvertiere Google Calendar Events in das Format von react-big-calendar
  const convertedEvents = useMemo(() => {
    return events
      .filter(event => 
        !searchTerm || 
        (event.summary && event.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      .map(event => {
        let start, end;
        if (event.start.dateTime) {
          start = new Date(event.start.dateTime);
          end = new Date(event.end.dateTime);
        } else if (event.start.date) {
          start = new Date(event.start.date);
          end = new Date(event.end.date);
          // Bei ganztägigen Events liefert Google als Enddatum den Folgetag – einen Tag abziehen:
          end.setDate(end.getDate() - 1);
        } else {
          start = new Date();
          end = new Date();
        }
        
        // Calculate event duration in days
        const durationInDays = moment(end).diff(moment(start), 'days') + 1;
        
        return {
          title: event.summary || 'Kein Titel',
          start,
          end,
          allDay: !!event.start.date,
          description: event.description || '',
          originalEvent: event,
          multiDay: durationInDays > 1,
          durationInDays
        };
      });
  }, [events, searchTerm]);

  // Event-Handler für das Öffnen des Detail-Modals
  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
  }, []);

  // Schließen des Detail-Modals
  const handleCloseModal = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  // Handle search with debounce
  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
  }, []);

  // Toggle view mode
  const toggleViewMode = useCallback(() => {
    setViewMode(prevMode => prevMode === 'grid' ? 'list' : 'grid');
  }, []);

  // Event-Rendering anpassen
  const eventStyleGetter = useCallback((event) => {
    let backgroundColor = '#0369a1';
    let textColor = '#ffffff';

    if (event.allDay) {
      backgroundColor = '#16a34a';
    }

    // Mobile-optimiertes Styling
    const isMobile = window.innerWidth < 768;
    
    return {
      style: {
        backgroundColor,
        color: textColor,
        borderRadius: '6px',
        border: 'none',
        padding: isMobile ? '1px 5px' : '2px 8px',
        fontWeight: '500',
        fontSize: isMobile ? '0.75rem' : '0.85rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    };
  }, []);

  // Sortierte Liste für die Listenansicht
  const sortedEvents = useMemo(() => {
    return [...convertedEvents].sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [convertedEvents]);

  // Render formatted date span for multi-day events
  const renderEventDateSpan = (event) => {
    if (event.multiDay) {
      return (
        <div className="event-date-range">
          <div className="event-date-day">{moment(event.start).format('DD')}</div>
          <div className="event-date-month">{moment(event.start).format('MMM')}</div>
          <div className="event-date-separator">-</div>
          <div className="event-date-day">{moment(event.end).format('DD')}</div>
          <div className="event-date-month">{moment(event.end).format('MMM')}</div>
          <span className="badge bg-primary">{event.durationInDays} Tage</span>
        </div>
      );
    } else {
      return (
        <>
          <div className="event-date-day">{moment(event.start).format('DD')}</div>
          <div className="event-date-month">{moment(event.start).format('MMM')}</div>
          {event.allDay ? (
            <span className="badge bg-success">Ganztägig</span>
          ) : (
            <div className="event-time">{moment(event.start).format('HH:mm')}</div>
          )}
        </>
      );
    }
  };

  // Calendar messages and formats memoized
  const calendarMessages = useMemo(() => ({
    next: 'Nächste',
    previous: 'Vorherige',
    today: 'Heute',
    month: 'Monat',
    week: 'Woche',
    day: 'Tag',
    agenda: 'Agenda',
    date: 'Datum',
    time: 'Zeit',
    event: 'Termin',
    noEventsInRange: 'Keine Termine in diesem Zeitraum',
    allDay: 'Ganztägig',
    showMore: (total) => `+ ${total} weitere`
  }), []);

  const calendarFormats = useMemo(() => ({
    monthHeaderFormat: 'MMMM YYYY',
    weekdayFormat: 'dd',
    dayHeaderFormat: 'dddd, DD. MMMM',
    dayRangeHeaderFormat: ({ start, end }) => `${moment(start).format('DD. MMM')} - ${moment(end).format('DD. MMM')}`
  }), []);

  return (
    <Container className="p-0">
      {/* Desktop Header – identisch zum LagerAdmin-Desktopheader */}
      <div className="d-none d-md-block py-4">
        <h2
          className="text-center mb-4"
          style={{
            color: '#333',
            fontWeight: '600',
            fontSize: '2.4rem',
            letterSpacing: '0.5px'
          }}
        >
          Kalender
        </h2>
        <div className="d-flex flex-column flex-md-row gap-2 mb-4 align-items-stretch px-3">
          <div className="flex-grow-1 custom-search-input d-flex">
            <div className="input-group">
              <span className="input-group-text">
                <Search size={16} />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Nach Terminen suchen..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="light" onClick={handlePrev} className="btn-nav">
              <ChevronLeft size={16} />
            </Button>
            <Button variant="light" onClick={handleToday} className="btn-nav">
              Heute
            </Button>
            <Button variant="light" onClick={handleNext} className="btn-nav">
              <ChevronRight size={16} />
            </Button>
            <Button 
              variant="light" 
              className="btn-nav" 
              onClick={toggleViewMode}
              title={viewMode === 'grid' ? 'Listenansicht' : 'Kalenderansicht'}
            >
              {viewMode === 'grid' ? <List size={16} /> : <Grid size={16} />}
            </Button>
            <Button
              className="btn-new-link"
              onClick={fetchEvents}
            >
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Header – identisch zum LagerAdmin-Mobileheader aber ohne Suchfunktion */}
      <div className="mobile-header d-md-none">
        <div className="d-flex justify-content-between align-items-center">
          <h2>Kalender</h2>
          <div className="mobile-actions">
            <div className="d-flex">
              <Button variant="light" onClick={handlePrev} className="btn-nav">
                <ChevronLeft size={16} />
              </Button>
              <Button variant="light" onClick={handleToday} className="btn-nav btn-today">
                Heute
              </Button>
              <Button variant="light" onClick={handleNext} className="btn-nav">
                <ChevronRight size={16} />
              </Button>
              <Button 
                variant="light" 
                className="btn-nav" 
                onClick={toggleViewMode}
                title={viewMode === 'grid' ? 'Listenansicht' : 'Kalenderansicht'}
              >
                {viewMode === 'grid' ? <List size={16} /> : <Grid size={16} />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Kalender-Card */}
      <Card className="shadow-sm desktop-card d-none d-md-block">
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Laden...</span>
              </Spinner>
            </div>
          ) : error ? (
            <div className="alert alert-danger text-center m-3" role="alert">
              {error}
            </div>
          ) : viewMode === 'list' ? (
            <div className="list-view-container py-2">
              {sortedEvents.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <CalendarIcon size={48} className="mb-3 text-muted" />
                  <p>Keine Termine gefunden</p>
                </div>
              ) : (
                <div className="event-list">
                  {sortedEvents.map((event, index) => (
                    <div 
                      key={index} 
                      className={`event-list-item ${event.multiDay ? 'multi-day-event' : ''}`}
                      onClick={() => handleSelectEvent(event)}
                    >
                      <div className="event-date">
                        {renderEventDateSpan(event)}
                      </div>
                      <div className="event-details">
                        <div className="event-title">
                          {event.title}
                          {event.multiDay && !event.allDay && (
                            <span className="event-time-range">
                              {moment(event.start).format('HH:mm')} – {moment(event.end).format('HH:mm')}
                            </span>
                          )}
                        </div>
                        {event.description && (
                          <div className="event-description-preview">
                            {event.description.length > 100 
                              ? `${event.description.substring(0, 100)}...` 
                              : event.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="calendar-container px-md-2 px-lg-3">
              <Calendar
                localizer={localizer}
                events={convertedEvents}
                date={currentDate}
                onNavigate={(date) => setCurrentDate(date)}
                toolbar={false}
                startAccessor="start"
                endAccessor="end"
                defaultView="month"
                views={['month', 'week', 'day']}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
                messages={calendarMessages}
                formats={calendarFormats}
              />
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* Mobile Inhaltsbereich - direkt integriert ohne Card */}
      <div className="d-md-none mobile-content">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Laden...</span>
            </Spinner>
          </div>
        ) : error ? (
          <div className="alert alert-danger text-center m-3" role="alert">
            {error}
          </div>
        ) : viewMode === 'list' ? (
          <div className="list-view-container-mobile">
            {sortedEvents.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <CalendarIcon size={48} className="mb-3 text-muted" />
                <p>Keine Termine gefunden</p>
              </div>
            ) : (
              <div className="event-list">
                {sortedEvents.map((event, index) => (
                  <div 
                    key={index} 
                    className={`event-list-item ${event.multiDay ? 'multi-day-event' : ''}`}
                    onClick={() => handleSelectEvent(event)}
                  >
                    <div className="event-date">
                      {renderEventDateSpan(event)}
                    </div>
                    <div className="event-details">
                      <div className="event-title">
                        {event.title}
                        {event.multiDay && !event.allDay && (
                          <span className="event-time-range">
                            {moment(event.start).format('HH:mm')} – {moment(event.end).format('HH:mm')}
                          </span>
                        )}
                      </div>
                      {event.description && (
                        <div className="event-description-preview">
                          {event.description.length > 100 
                            ? `${event.description.substring(0, 100)}...` 
                            : event.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="calendar-container-mobile px-3">
            <Calendar
              localizer={localizer}
              events={convertedEvents}
              date={currentDate}
              onNavigate={(date) => setCurrentDate(date)}
              toolbar={false}
              startAccessor="start"
              endAccessor="end"
              defaultView="month"
              views={['month', 'week', 'day']}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventStyleGetter}
              messages={calendarMessages}
              formats={calendarFormats}
            />
          </div>
        )}
      </div>

      {/* Zusätzlicher Abstand am Ende für Mobile und Desktop */}
      <div className="mobile-bottom-spacing"></div>

      {/* Modal für Event-Details */}
      <Modal show={!!selectedEvent} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedEvent?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <>
              <p className="mb-3">
                <span className="event-label">Datum:</span>
                {selectedEvent.allDay ? (
                  selectedEvent.multiDay ? (
                    <span className="event-date-multiday">
                      {moment(selectedEvent.start).format('LL')} - {moment(selectedEvent.end).format('LL')}
                      <br />
                      <span className="badge bg-primary mt-1">{selectedEvent.durationInDays} Tage</span>
                    </span>
                  ) : (
                    moment(selectedEvent.start).format('LL')
                  )
                ) : (
                  selectedEvent.multiDay ? (
                    <span className="event-time">
                      {moment(selectedEvent.start).format('LL')} - {moment(selectedEvent.end).format('LL')}
                      <br />
                      {moment(selectedEvent.start).format('HH:mm')} – {moment(selectedEvent.end).format('HH:mm')} Uhr
                      <br />
                      <span className="badge bg-primary mt-1">{selectedEvent.durationInDays} Tage</span>
                    </span>
                  ) : (
                    <span className="event-time">
                      {moment(selectedEvent.start).format('LL')}<br />
                      {moment(selectedEvent.start).format('HH:mm')} – {moment(selectedEvent.end).format('HH:mm')} Uhr
                    </span>
                  )
                )}
              </p>
              {selectedEvent.description && (
                <div className="event-description">
                  <span className="event-label">Beschreibung:</span>
                  <div className="description-content mt-2">
                    {selectedEvent.description}
                  </div>
                </div>
              )}
              {selectedEvent.originalEvent && selectedEvent.originalEvent.location && (
                <p className="mt-3">
                  <span className="event-label">Ort:</span>
                  <span className="event-location">{selectedEvent.originalEvent.location}</span>
                </p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Styles */}
      <style type="text/css">
        {`
          body {
            background-color: rgb(243, 242, 241) !important;
          }
          
          /* Mobile Header Styles */
          .mobile-header {
            background-color: #F3F2F1;
            border-bottom: 1px solid rgba(0,0,0,0.1);
            padding: 1rem;
            margin-bottom: 1rem;
          }
          .mobile-header h2 {
            font-weight: bold;
            margin: 0;
            font-size: 2rem;
            color: #333;
          }
          .mobile-actions {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .mobile-search {
            padding: 0 1rem;
          }
          /* Abstand zwischen Kalender und Seitenende für alle Bildschirmgrößen */
          .mobile-bottom-spacing {
            height: 40px;
            display: block;
            width: 100%;
          }
          
          /* Button Styles */
          .btn-icon {
            background: none;
            border: none;
            width: 40px;
            height: 40px;
            padding: 0;
            border-radius: 20px;
            color: #333;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            position: relative;
            z-index: 2;
          }
          .btn-icon:hover {
            background-color: rgba(0,0,0,0.05);
            color: #0078d4;
          }
          .btn-icon.active {
            background-color: #f0f0f0;
            color: #0078d4;
          }
          .btn-nav {
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 0.6rem 1rem;
            background-color: #f8f9fa;
            color: #333;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .btn-nav:hover {
            background-color: #e9ecef;
            border-color: #ced4da;
          }
          .btn-today {
            min-width: 70px;
          }
          .btn-new-link {
            background-color: #333;
            color: #fff;
            border: none;
            padding: 0.6rem 1.2rem;
            border-radius: 6px;
            font-size: 1rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            white-space: nowrap;
          }
          .btn-new-link:hover {
            background-color: #222;
            color: white;
          }
          .btn-new-link:focus {
            outline: none;
            box-shadow: 0 0 0 2px rgba(0,120,212,0.3);
            color: white;
          }
          
          /* Search Input Styles */
          .custom-search-input .input-group-text {
            border-radius: 6px 0 0 6px;
            border: 1px solid #ddd;
            background-color: #f7f7f7;
            transition: background-color 0.2s, border-color 0.2s;
          }
          .custom-search-input .input-group-text:hover {
            background-color: #ededed;
          }
          .custom-search-input .form-control {
            border: 1px solid #ddd;
            border-left: none;
            border-radius: 0 6px 6px 0;
            background-color: #fafafa;
            transition: background-color 0.2s, box-shadow 0.2s, border-color 0.2s;
          }
          .custom-search-input .form-control:focus {
            box-shadow: 0 0 0 2px rgba(0,120,212,0.2);
            outline: none;
            background-color: #fff;
            border-color: #0078d4;
          }
          
          /* Calendar Container Styles */
          .calendar-container {
            height: 80vh;
            max-height: 800px;
            padding: 8px;
          }
          
          .calendar-container-mobile {
            height: calc(100vh - 180px); /* Mehr Platz für den unteren Rand */
            background-color: #F3F2F1;
          }
          
          .mobile-content {
            background-color: #F3F2F1;
          }
          
          /* React Big Calendar Custom Styles */
          .rbc-calendar {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            width: 100%;
            border-radius: 8px;
            overflow: hidden;
          }
          .rbc-toolbar {
            margin-bottom: 20px;
          }
          .rbc-toolbar button {
            color: #333;
            border: 1px solid #ddd;
            border-radius: 5px;
            padding: 6px 12px;
          }
          .rbc-toolbar button.rbc-active {
            background-color: #0284c7;
            color: white;
          }
          .rbc-header {
            padding: 10px 3px;
            font-weight: 600;
            background-color: #f8f9fa;
            border-bottom: 1px solid #e9ecef;
          }
          .rbc-month-view, .rbc-time-view {
            border: 1px solid #e9ecef;
            border-radius: 8px;
            overflow: hidden;
          }
          
          @media (max-width: 767.98px) {
            .rbc-month-view, .rbc-time-view {
              margin: 0 8px;
            }
          }
          .rbc-date-cell {
            padding: 5px 8px;
            text-align: center;
            font-weight: 500;
          }
          .rbc-off-range-bg {
            background-color: #f8f9fa;
          }
          .rbc-off-range {
            color: #adb5bd;
          }
          .rbc-today {
            background-color: #e6f7ff;
          }
          .rbc-day-bg {
            transition: background-color 0.2s;
          }
          .rbc-day-bg:hover {
            background-color: #f0f9ff;
          }
          .rbc-event {
            border-radius: 4px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .rbc-event:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          .rbc-show-more {
            font-weight: 500;
            color: #0369a1;
            background-color: transparent;
          }
          .rbc-show-more:hover {
            color: #0284c7;
            background-color: #f0f9ff;
          }
          .rbc-agenda-empty {
            text-align: center;
            padding: 40px;
            font-style: italic;
            color: #6c757d;
          }
          
          /* Event Modal Styles */
          .event-label {
            display: block;
            font-weight: 600;
            color: #333;
            margin-bottom: 5px;
          }
          .event-time {
            color: #444;
            display: block;
          }
          .event-date-multiday {
            color: #444;
            display: block;
          }
          .event-description {
            margin-top: 15px;
          }
          .description-content {
            background-color: #f8f9fa;
            padding: 12px;
            border-radius: 8px;
            white-space: pre-line;
          }
          .event-location {
            display: block;
            color: #0369a1;
          }
          
          /* List View Styles */
          .list-view-container {
            height: 80vh;
            overflow-y: auto;
            background-color: #ffffff;
            border-radius: 8px;
          }
          .list-view-container-mobile {
            height: calc(100vh - 180px); /* Mehr Platz für den unteren Rand */
            overflow-y: auto;
            background-color: #F3F2F1;
            width: 100%;
            max-width: 100%;
            overflow-x: hidden;
            padding-bottom: 20px;
          }
          
          .event-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 8px;
          }
          
          /* Verbesserte Styles für Listenelemente und mehrtägige Events */
          .event-list-item {
            display: flex;
            background-color: #ffffff;
            border-radius: 8px;
            padding: 12px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
            cursor: pointer;
            transition: transform 0.2s, box-shadow 0.2s;
            gap: 12px;
            max-width: 100%;
            position: relative;
          }
          .event-list-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 3px 6px rgba(0,0,0,0.1);
            background-color: #f8fbff;
          }
          
          /* Mehrtägige Events spezifische Styles */
          .multi-day-event {
            /* Kein Border mehr auf der linken Seite */
          }
          .multi-day-event.event-list-item:hover {
            background-color: #f0f7ff;
          }
          
          .event-date {
            display: flex;
            flex-direction: column;
            align-items: center;
            min-width: 60px;
            text-align: center;
          }
          
          /* Styles für Mehrtägige Events in der Listenansicht */
          .event-date-range {
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
          }
          
          .event-date-separator {
            margin: 2px 0;
            font-weight: bold;
            color: #666;
          }
          
          .event-date-day {
            font-size: 1.5rem;
            font-weight: 700;
            line-height: 1;
            color: #333;
          }
          .event-date-month {
            font-size: 0.85rem;
            font-weight: 500;
            color: #666;
            text-transform: uppercase;
          }
          .event-time {
            font-size: 0.8rem;
            color: #666;
            margin-top: 4px;
          }
          .event-time-range {
            font-size: 0.8rem;
            color: #666;
            margin-left: 8px;
            white-space: nowrap;
          }
          .event-details {
            flex: 1;
            min-width: 0; /* Needed for text-overflow to work */
          }
          .event-title {
            font-weight: 600;
            color: #333;
            margin-bottom: 4px;
            font-size: 0.95rem;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
          }
          .event-description-preview {
            font-size: 0.85rem;
            color: #666;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
          .badge.bg-success {
            font-weight: 500;
            padding: 0.25em 0.6em;
            font-size: 0.75em;
            border-radius: 4px;
            background-color: #16a34a !important;
            margin-top: 4px;
          }
          .badge.bg-primary {
            font-weight: 500;
            padding: 0.25em 0.6em;
            font-size: 0.75em;
            border-radius: 4px;
            background-color: #0369a1 !important;
            margin-top: 4px;
          }
          
          /* Media Queries für bessere Mobile Experience */
          @media (max-width: 767.98px) {
            .calendar-container {
              height: 75vh;
              padding: 0 12px;
            }
            .rbc-toolbar {
              flex-direction: column;
              align-items: flex-start;
              gap: 10px;
            }
            .rbc-toolbar-label {
              margin: 10px 0;
              text-align: left;
            }
            .rbc-header {
              padding: 5px 2px;
              font-size: 0.85rem;
            }
            .rbc-date-cell {
              padding: 3px;
              font-size: 0.85rem;
            }
            .rbc-event {
              font-size: 0.75rem;
              padding: 1px 3px;
              margin: 0 1px;
            }
            .btn-nav {
              padding: 0.5rem;
              min-width: 40px;
            }
            .btn-today {
              min-width: 60px;
            }
            .multi-day-event .event-date-range {
              font-size: 0.9rem;
            }
          }
        `}
      </style>
    </Container>
  );
};

export default CalendarPage;